<template>
    <a-config-provider :locale="arcoLocale">
        <router-view/>
    </a-config-provider>
</template>

<script>
import common from './common/api/common';
import util from './common/util';
import {v4 as uuidv4} from 'uuid';
import { handle401Error, isPublicPath } from './common/util/authHelper';

import {mapMutations, mapGetters} from 'vuex';
import PageNav from "@/components/PageNav/PageNav.vue";
import Footer from "@/components/Footer/footer.vue";
import {ConfigProvider} from '@arco-design/web-vue';

export default {
    name: 'App',
    components: {
        PageNav,
        Footer,
        ConfigProvider
    },
    computed: {
        ...mapGetters(['userTheme', 'userThemeIndex']),
        arcoLocale() {
            // 根据当前语言返回ArcoVue的语言配置
            const currentLang = this.$i18n?.locale || 'en-US';
            console.log('🌐 当前语言:', currentLang);
            
            // 确保语言值有效
            if (!currentLang || typeof currentLang !== 'string') {
                console.warn('⚠️ 无效的语言设置，使用默认英文:', currentLang);
                return this.getEnglishLocale();
            }
            
            // 根据语言代码返回对应的locale配置
            switch (currentLang) {
                case 'en-US':
                    console.log('🇺🇸 切换到ArcoVue英文');
                    return this.getEnglishLocale();
                case 'th':
                    console.log('🇹🇭 切换到ArcoVue泰语');
                    return this.getThaiLocale();
                case 'ja':
                    console.log('🇯🇵 切换到ArcoVue日语');
                    return this.getJapaneseLocale();
                case 'ko':
                    console.log('🇰🇷 切换到ArcoVue韩语');
                    return this.getKoreanLocale();
                case 'ru':
                    console.log('🇷🇺 切换到ArcoVue俄语');
                    return this.getRussianLocale();
                case 'gm':
                    console.log('🇩🇪 切换到ArcoVue德语');
                    return this.getGermanLocale();
                case 'au':
                    console.log('🇦🇺 切换到ArcoVue澳大利亚英语');
                    return this.getAustralianLocale();
                case 'zh_Hant':
                    console.log('🇹🇼 切换到ArcoVue繁体中文');
                    return this.getTraditionalChineseLocale();
                case 'zh_CN':
                    console.log('🇨🇳 切换到ArcoVue简体中文');
                    return this.getChineseLocale();
                default:
                    console.log('🇺🇸 切换到ArcoVue英文（默认）');
                    return this.getEnglishLocale();
            }
        }
    },

    watch: {
        '$i18n.locale': {
            handler(newLocale) {
                // 当Vue I18n语言改变时，同步更新ArcoVue语言和K线图语言包
                console.log('🔄 Vue I18n语言改变:', newLocale);
                
                // 更新K线图语言包
                const klineLocaleMap = {
                    'en-US': 'en-US',
                    'zh_CN': 'zh-CN',
                    'zh_Hant': 'zh-CN',
                    'ru': 'en-US',
                    'th': 'en-US',
                    'ja': 'en-US',
                    'ko': 'en-US',
                    'au': 'en-US',
                    'gm': 'en-US',
                    'fr': 'en-US',
                    'es': 'en-US',
                    'vi': 'en-US'
                };
                const klineLocale = klineLocaleMap[newLocale] || 'en-US';
                
                // KLineChartPro已内置zh-CN和en-US语言包，无需手动加载
                // 语言切换时图表会重新创建，自动使用新的locale设置
                console.log('🔧 K线图语言已更新:', klineLocale);
                
                this.$nextTick(() => {
                    // 重新设置ArcoVue国际化
                    this.setupArcoI18n();
                    // 强制更新ConfigProvider的locale
                    this.$forceUpdate();
                    console.log('✅ ArcoVue语言已同步:', this.arcoLocale);
                });
            },
            immediate: true
        },
        userTheme: {
            handler(theme) {
                this.applyTheme(theme);
            },
            immediate: true
        }
    },
    created() {
        this.initTheme();
    },
    mounted() {
        let uuid = util.getCache('deviceId', '');
        if (uuid === '') {
            uuid = uuidv4();
            util.setCache('deviceId', uuid);
        }
        this.getSiteConfig();
        this.$store.dispatch('initializeDisplayCurrency').catch((error) => {
            console.error('❌ 货币设置失败:', error);
        });
        // 关闭自动语言检测，使用统一英文
        // this.getLocal();
        this.getServerTime();
        this.autoLogin();

        // 添加全局错误处理
        this.setupGlobalErrorHandling();
        
        // 设置ArcoVue全局国际化
        this.setupArcoI18n();
    },
    methods: {
        ...mapMutations(['setConfig', 'userLogin']),
        
        // 获取中文语言配置
        getChineseLocale() {
            return {
                locale: 'zh-CN',
                pagination: {
                    total: '共 {total} 条',
                    pageSize: '每页条数',
                    goto: '前往',
                    page: '页',
                    prev: '上一页',
                    next: '下一页',
                    prev5: '向前 5 页',
                    next5: '向后 5 页',
                    itemsPerPage: '条/页',
                    jumpTo: '跳至',
                    pageOf: '第 {current} 页，共 {total} 页',
                    countPerPage: '每页条数'
                },
                datePicker: {
                    now: '此刻',
                    today: '今天',
                    cancel: '取消',
                    clear: '清空',
                    confirm: '确定',
                    selectDate: '选择日期',
                    selectTime: '选择时间',
                    startDate: '开始日期',
                    endDate: '结束日期',
                    startTime: '开始时间',
                    endTime: '结束时间',
                    year: '年',
                    month: '月',
                    week: '周',
                    day: '日',
                    hour: '时',
                    minute: '分',
                    second: '秒',
                    yearFormat: 'YYYY年',
                    monthFormat: 'MM月',
                    weekFormat: '第wo周',
                    dayFormat: 'DD日',
                    hourFormat: 'HH时',
                    minuteFormat: 'mm分',
                    secondFormat: 'ss秒',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: true,
                    view: {
                        month: '月',
                        year: '年',
                        week: '周',
                        day: '日'
                    },
                    month: {
                        long: {
                            January: '一月',
                            February: '二月',
                            March: '三月',
                            April: '四月',
                            May: '五月',
                            June: '六月',
                            July: '七月',
                            August: '八月',
                            September: '九月',
                            October: '十月',
                            November: '十一月',
                            December: '十二月'
                        },
                        short: {
                            January: '1月',
                            February: '2月',
                            March: '3月',
                            April: '4月',
                            May: '5月',
                            June: '6月',
                            July: '7月',
                            August: '8月',
                            September: '9月',
                            October: '10月',
                            November: '11月',
                            December: '12月'
                        }
                    },
                    week: {
                        long: {
                            self: '周',
                            monday: '星期一',
                            tuesday: '星期二',
                            wednesday: '星期三',
                            thursday: '星期四',
                            friday: '星期五',
                            saturday: '星期六',
                            sunday: '星期日'
                        },
                        short: {
                            self: '周',
                            monday: '一',
                            tuesday: '二',
                            wednesday: '三',
                            thursday: '四',
                            friday: '五',
                            saturday: '六',
                            sunday: '日'
                        }
                    },
                    placeholder: {
                        date: '请选择日期',
                        week: '请选择周',
                        month: '请选择月份',
                        year: '请选择年份',
                        quarter: '请选择季度',
                        time: '请选择时间'
                    },
                    rangePlaceholder: {
                        date: ['开始日期', '结束日期'],
                        week: ['开始周', '结束周'],
                        month: ['开始月份', '结束月份'],
                        year: ['开始年份', '结束年份'],
                        quarter: ['开始季度', '结束季度'],
                        time: ['开始时间', '结束时间']
                    }
                }
            };
        },
        
        // 获取英文语言配置
        getEnglishLocale() {
            return {
                locale: 'en-US',
                pagination: {
                    total: 'Total {total} items',
                    pageSize: 'Page Size',
                    goto: 'Go to',
                    page: 'Page',
                    prev: 'Previous',
                    next: 'Next',
                    prev5: 'Previous 5 Pages',
                    next5: 'Next 5 Pages',
                    itemsPerPage: 'items per page',
                    jumpTo: 'Jump to',
                    pageOf: 'Page {current} of {total}',
                    countPerPage: 'items per page'
                },
                datePicker: {
                    now: 'Now',
                    today: 'Today',
                    cancel: 'Cancel',
                    clear: 'Clear',
                    confirm: 'OK',
                    selectDate: 'Select date',
                    selectTime: 'Select time',
                    startDate: 'Start date',
                    endDate: 'End date',
                    startTime: 'Start time',
                    endTime: 'End time',
                    year: 'Year',
                    month: 'Month',
                    week: 'Week',
                    day: 'Day',
                    hour: 'Hour',
                    minute: 'Minute',
                    second: 'Second',
                    yearFormat: 'YYYY',
                    monthFormat: 'MM',
                    weekFormat: 'wo',
                    dayFormat: 'DD',
                    hourFormat: 'HH',
                    minuteFormat: 'mm',
                    secondFormat: 'ss',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: false,
                    view: {
                        month: 'Month',
                        year: 'Year',
                        week: 'Week',
                        day: 'Day'
                    },
                    month: {
                        long: {
                            January: 'January',
                            February: 'February',
                            March: 'March',
                            April: 'April',
                            May: 'May',
                            June: 'June',
                            July: 'July',
                            August: 'August',
                            September: 'September',
                            October: 'October',
                            November: 'November',
                            December: 'December'
                        },
                        short: {
                            January: 'Jan',
                            February: 'Feb',
                            March: 'Mar',
                            April: 'Apr',
                            May: 'May',
                            June: 'Jun',
                            July: 'Jul',
                            August: 'Aug',
                            September: 'Sep',
                            October: 'Oct',
                            November: 'Nov',
                            December: 'Dec'
                        }
                    },
                    week: {
                        long: {
                            self: 'Week',
                            monday: 'Monday',
                            tuesday: 'Tuesday',
                            wednesday: 'Wednesday',
                            thursday: 'Thursday',
                            friday: 'Friday',
                            saturday: 'Saturday',
                            sunday: 'Sunday'
                        },
                        short: {
                            self: 'Week',
                            monday: 'Mon',
                            tuesday: 'Tue',
                            wednesday: 'Wed',
                            thursday: 'Thu',
                            friday: 'Fri',
                            saturday: 'Sat',
                            sunday: 'Sun'
                        }
                    },
                    placeholder: {
                        date: 'Please select date',
                        week: 'Please select week',
                        month: 'Please select month',
                        year: 'Please select year',
                        quarter: 'Please select quarter',
                        time: 'Please select time'
                    },
                    rangePlaceholder: {
                        date: ['Start date', 'End date'],
                        week: ['Start week', 'End week'],
                        month: ['Start month', 'End month'],
                        year: ['Start year', 'End year'],
                        quarter: ['Start quarter', 'End quarter'],
                        time: ['Start time', 'End time']
                    }
                }
            };
        },
        
        // 获取泰语语言配置
        getThaiLocale() {
            return {
                locale: 'th-TH',
                pagination: {
                    total: 'ทั้งหมด {total} รายการ',
                    pageSize: 'ขนาดหน้า',
                    goto: 'ไปที่',
                    page: 'หน้า',
                    prev: 'ก่อนหน้า',
                    next: 'ถัดไป',
                    prev5: 'ก่อนหน้า 5 หน้า',
                    next5: 'ถัดไป 5 หน้า',
                    itemsPerPage: 'รายการต่อหน้า',
                    jumpTo: 'ไปที่',
                    pageOf: 'หน้า {current} จาก {total}',
                    countPerPage: 'รายการต่อหน้า'
                },
                datePicker: {
                    now: 'ตอนนี้',
                    today: 'วันนี้',
                    cancel: 'ยกเลิก',
                    clear: 'ล้าง',
                    confirm: 'ยืนยัน',
                    selectDate: 'เลือกวันที่',
                    selectTime: 'เลือกเวลา',
                    startDate: 'วันที่เริ่มต้น',
                    endDate: 'วันที่สิ้นสุด',
                    startTime: 'เวลาเริ่มต้น',
                    endTime: 'เวลาสิ้นสุด',
                    year: 'ปี',
                    month: 'เดือน',
                    week: 'สัปดาห์',
                    day: 'วัน',
                    hour: 'ชั่วโมง',
                    minute: 'นาที',
                    second: 'วินาที',
                    yearFormat: 'YYYY',
                    monthFormat: 'MM',
                    weekFormat: 'wo',
                    dayFormat: 'DD',
                    hourFormat: 'HH',
                    minuteFormat: 'mm',
                    secondFormat: 'ss',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: false,
                    view: {
                        month: 'เดือน',
                        year: 'ปี',
                        week: 'สัปดาห์',
                        day: 'วัน'
                    },
                    month: {
                        long: {
                            January: 'มกราคม',
                            February: 'กุมภาพันธ์',
                            March: 'มีนาคม',
                            April: 'เมษายน',
                            May: 'พฤษภาคม',
                            June: 'มิถุนายน',
                            July: 'กรกฎาคม',
                            August: 'สิงหาคม',
                            September: 'กันยายน',
                            October: 'ตุลาคม',
                            November: 'พฤศจิกายน',
                            December: 'ธันวาคม'
                        },
                        short: {
                            January: 'ม.ค.',
                            February: 'ก.พ.',
                            March: 'มี.ค.',
                            April: 'เม.ย.',
                            May: 'พ.ค.',
                            June: 'มิ.ย.',
                            July: 'ก.ค.',
                            August: 'ส.ค.',
                            September: 'ก.ย.',
                            October: 'ต.ค.',
                            November: 'พ.ย.',
                            December: 'ธ.ค.'
                        }
                    },
                    week: {
                        long: {
                            self: 'สัปดาห์',
                            monday: 'วันจันทร์',
                            tuesday: 'วันอังคาร',
                            wednesday: 'วันพุธ',
                            thursday: 'วันพฤหัสบดี',
                            friday: 'วันศุกร์',
                            saturday: 'วันเสาร์',
                            sunday: 'วันอาทิตย์'
                        },
                        short: {
                            self: 'สัปดาห์',
                            monday: 'จ.',
                            tuesday: 'อ.',
                            wednesday: 'พ.',
                            thursday: 'พฤ.',
                            friday: 'ศ.',
                            saturday: 'ส.',
                            sunday: 'อา.'
                        }
                    },
                    placeholder: {
                        date: 'กรุณาเลือกวันที่',
                        week: 'กรุณาเลือกสัปดาห์',
                        month: 'กรุณาเลือกเดือน',
                        year: 'กรุณาเลือกปี',
                        quarter: 'กรุณาเลือกไตรมาส',
                        time: 'กรุณาเลือกเวลา'
                    },
                    rangePlaceholder: {
                        date: ['วันที่เริ่มต้น', 'วันที่สิ้นสุด'],
                        week: ['สัปดาห์เริ่มต้น', 'สัปดาห์สิ้นสุด'],
                        month: ['เดือนเริ่มต้น', 'เดือนสิ้นสุด'],
                        year: ['ปีเริ่มต้น', 'ปีสิ้นสุด'],
                        quarter: ['ไตรมาสเริ่มต้น', 'ไตรมาสสิ้นสุด'],
                        time: ['เวลาเริ่มต้น', 'เวลาสิ้นสุด']
                    }
                }
            };
        },
        
        // 获取日语语言配置
        getJapaneseLocale() {
            return {
                locale: 'ja-JP',
                pagination: {
                    total: '合計 {total} 件',
                    pageSize: 'ページサイズ',
                    goto: '移動',
                    page: 'ページ',
                    prev: '前へ',
                    next: '次へ',
                    prev5: '前5ページ',
                    next5: '次5ページ',
                    itemsPerPage: '件/ページ',
                    jumpTo: 'ジャンプ',
                    pageOf: '{current} / {total} ページ',
                    countPerPage: '件/ページ'
                },
                datePicker: {
                    now: '今',
                    today: '今日',
                    cancel: 'キャンセル',
                    clear: 'クリア',
                    confirm: '確定',
                    selectDate: '日付を選択',
                    selectTime: '時間を選択',
                    startDate: '開始日',
                    endDate: '終了日',
                    startTime: '開始時間',
                    endTime: '終了時間',
                    year: '年',
                    month: '月',
                    week: '週',
                    day: '日',
                    hour: '時',
                    minute: '分',
                    second: '秒',
                    yearFormat: 'YYYY年',
                    monthFormat: 'MM月',
                    weekFormat: '第wo週',
                    dayFormat: 'DD日',
                    hourFormat: 'HH時',
                    minuteFormat: 'mm分',
                    secondFormat: 'ss秒',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: true,
                    view: {
                        month: '月',
                        year: '年',
                        week: '週',
                        day: '日'
                    },
                    month: {
                        long: {
                            January: '1月',
                            February: '2月',
                            March: '3月',
                            April: '4月',
                            May: '5月',
                            June: '6月',
                            July: '7月',
                            August: '8月',
                            September: '9月',
                            October: '10月',
                            November: '11月',
                            December: '12月'
                        },
                        short: {
                            January: '1月',
                            February: '2月',
                            March: '3月',
                            April: '4月',
                            May: '5月',
                            June: '6月',
                            July: '7月',
                            August: '8月',
                            September: '9月',
                            October: '10月',
                            November: '11月',
                            December: '12月'
                        }
                    },
                    week: {
                        long: {
                            self: '週',
                            monday: '月曜日',
                            tuesday: '火曜日',
                            wednesday: '水曜日',
                            thursday: '木曜日',
                            friday: '金曜日',
                            saturday: '土曜日',
                            sunday: '日曜日'
                        },
                        short: {
                            self: '週',
                            monday: '月',
                            tuesday: '火',
                            wednesday: '水',
                            thursday: '木',
                            friday: '金',
                            saturday: '土',
                            sunday: '日'
                        }
                    },
                    placeholder: {
                        date: '日付を選択してください',
                        week: '週を選択してください',
                        month: '月を選択してください',
                        year: '年を選択してください',
                        quarter: '四半期を選択してください',
                        time: '時間を選択してください'
                    },
                    rangePlaceholder: {
                        date: ['開始日', '終了日'],
                        week: ['開始週', '終了週'],
                        month: ['開始月', '終了月'],
                        year: ['開始年', '終了年'],
                        quarter: ['開始四半期', '終了四半期'],
                        time: ['開始時間', '終了時間']
                    }
                }
            };
        },
        
        // 获取韩语语言配置
        getKoreanLocale() {
            return {
                locale: 'ko-KR',
                pagination: {
                    total: '총 {total}개',
                    pageSize: '페이지 크기',
                    goto: '이동',
                    page: '페이지',
                    prev: '이전',
                    next: '다음',
                    prev5: '이전 5페이지',
                    next5: '다음 5페이지',
                    itemsPerPage: '개/페이지',
                    jumpTo: '이동',
                    pageOf: '{current} / {total} 페이지',
                    countPerPage: '개/페이지'
                },
                datePicker: {
                    now: '지금',
                    today: '오늘',
                    cancel: '취소',
                    clear: '지우기',
                    confirm: '확인',
                    selectDate: '날짜 선택',
                    selectTime: '시간 선택',
                    startDate: '시작일',
                    endDate: '종료일',
                    startTime: '시작 시간',
                    endTime: '종료 시간',
                    year: '년',
                    month: '월',
                    week: '주',
                    day: '일',
                    hour: '시',
                    minute: '분',
                    second: '초',
                    yearFormat: 'YYYY년',
                    monthFormat: 'MM월',
                    weekFormat: '제wo주',
                    dayFormat: 'DD일',
                    hourFormat: 'HH시',
                    minuteFormat: 'mm분',
                    secondFormat: 'ss초',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: true,
                    view: {
                        month: '월',
                        year: '년',
                        week: '주',
                        day: '일'
                    },
                    month: {
                        long: {
                            January: '1월',
                            February: '2월',
                            March: '3월',
                            April: '4월',
                            May: '5월',
                            June: '6월',
                            July: '7월',
                            August: '8월',
                            September: '9월',
                            October: '10월',
                            November: '11월',
                            December: '12월'
                        },
                        short: {
                            January: '1월',
                            February: '2월',
                            March: '3월',
                            April: '4월',
                            May: '5월',
                            June: '6월',
                            July: '7월',
                            August: '8월',
                            September: '9월',
                            October: '10월',
                            November: '11월',
                            December: '12월'
                        }
                    },
                    week: {
                        long: {
                            self: '주',
                            monday: '월요일',
                            tuesday: '화요일',
                            wednesday: '수요일',
                            thursday: '목요일',
                            friday: '금요일',
                            saturday: '토요일',
                            sunday: '일요일'
                        },
                        short: {
                            self: '주',
                            monday: '월',
                            tuesday: '화',
                            wednesday: '수',
                            thursday: '목',
                            friday: '금',
                            saturday: '토',
                            sunday: '일'
                        }
                    },
                    placeholder: {
                        date: '날짜를 선택하세요',
                        week: '주를 선택하세요',
                        month: '월을 선택하세요',
                        year: '년을 선택하세요',
                        quarter: '분기를 선택하세요',
                        time: '시간을 선택하세요'
                    },
                    rangePlaceholder: {
                        date: ['시작일', '종료일'],
                        week: ['시작주', '종료주'],
                        month: ['시작월', '종료월'],
                        year: ['시작년', '종료년'],
                        quarter: ['시작분기', '종료분기'],
                        time: ['시작시간', '종료시간']
                    }
                }
            };
        },
        
        // 获取俄语语言配置
        getRussianLocale() {
            return {
                locale: 'ru-RU',
                pagination: {
                    total: 'Всего {total} элементов',
                    pageSize: 'Размер страницы',
                    goto: 'Перейти',
                    page: 'Страница',
                    prev: 'Предыдущая',
                    next: 'Следующая',
                    prev5: 'Предыдущие 5 страниц',
                    next5: 'Следующие 5 страниц',
                    itemsPerPage: 'элементов на странице',
                    jumpTo: 'Перейти к',
                    pageOf: 'Страница {current} из {total}',
                    countPerPage: 'элементов на странице'
                },
                datePicker: {
                    now: 'Сейчас',
                    today: 'Сегодня',
                    cancel: 'Отмена',
                    clear: 'Очистить',
                    confirm: 'ОК',
                    selectDate: 'Выбрать дату',
                    selectTime: 'Выбрать время',
                    startDate: 'Дата начала',
                    endDate: 'Дата окончания',
                    startTime: 'Время начала',
                    endTime: 'Время окончания',
                    year: 'Год',
                    month: 'Месяц',
                    week: 'Неделя',
                    day: 'День',
                    hour: 'Час',
                    minute: 'Минута',
                    second: 'Секунда',
                    yearFormat: 'YYYY',
                    monthFormat: 'MM',
                    weekFormat: 'wo',
                    dayFormat: 'DD',
                    hourFormat: 'HH',
                    minuteFormat: 'mm',
                    secondFormat: 'ss',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: false,
                    view: {
                        month: 'Месяц',
                        year: 'Год',
                        week: 'Неделя',
                        day: 'День'
                    },
                    month: {
                        long: {
                            January: 'Январь',
                            February: 'Февраль',
                            March: 'Март',
                            April: 'Апрель',
                            May: 'Май',
                            June: 'Июнь',
                            July: 'Июль',
                            August: 'Август',
                            September: 'Сентябрь',
                            October: 'Октябрь',
                            November: 'Ноябрь',
                            December: 'Декабрь'
                        },
                        short: {
                            January: 'Янв',
                            February: 'Фев',
                            March: 'Мар',
                            April: 'Апр',
                            May: 'Май',
                            June: 'Июн',
                            July: 'Июл',
                            August: 'Авг',
                            September: 'Сен',
                            October: 'Окт',
                            November: 'Ноя',
                            December: 'Дек'
                        }
                    },
                    week: {
                        long: {
                            self: 'Неделя',
                            monday: 'Понедельник',
                            tuesday: 'Вторник',
                            wednesday: 'Среда',
                            thursday: 'Четверг',
                            friday: 'Пятница',
                            saturday: 'Суббота',
                            sunday: 'Воскресенье'
                        },
                        short: {
                            self: 'Неделя',
                            monday: 'Пн',
                            tuesday: 'Вт',
                            wednesday: 'Ср',
                            thursday: 'Чт',
                            friday: 'Пт',
                            saturday: 'Сб',
                            sunday: 'Вс'
                        }
                    },
                    placeholder: {
                        date: 'Выберите дату',
                        week: 'Выберите неделю',
                        month: 'Выберите месяц',
                        year: 'Выберите год',
                        quarter: 'Выберите квартал',
                        time: 'Выберите время'
                    },
                    rangePlaceholder: {
                        date: ['Дата начала', 'Дата окончания'],
                        week: ['Неделя начала', 'Неделя окончания'],
                        month: ['Месяц начала', 'Месяц окончания'],
                        year: ['Год начала', 'Год окончания'],
                        quarter: ['Квартал начала', 'Квартал окончания'],
                        time: ['Время начала', 'Время окончания']
                    }
                }
            };
        },
        
        // 获取德语语言配置
        getGermanLocale() {
            return {
                locale: 'de-DE',
                pagination: {
                    total: 'Insgesamt {total} Elemente',
                    pageSize: 'Seitengröße',
                    goto: 'Gehe zu',
                    page: 'Seite',
                    prev: 'Vorherige',
                    next: 'Nächste',
                    prev5: 'Vorherige 5 Seiten',
                    next5: 'Nächste 5 Seiten',
                    itemsPerPage: 'Elemente pro Seite',
                    jumpTo: 'Springe zu',
                    pageOf: 'Seite {current} von {total}',
                    countPerPage: 'Elemente pro Seite'
                },
                datePicker: {
                    now: 'Jetzt',
                    today: 'Heute',
                    cancel: 'Abbrechen',
                    clear: 'Löschen',
                    confirm: 'OK',
                    selectDate: 'Datum auswählen',
                    selectTime: 'Zeit auswählen',
                    startDate: 'Startdatum',
                    endDate: 'Enddatum',
                    startTime: 'Startzeit',
                    endTime: 'Endzeit',
                    year: 'Jahr',
                    month: 'Monat',
                    week: 'Woche',
                    day: 'Tag',
                    hour: 'Stunde',
                    minute: 'Minute',
                    second: 'Sekunde',
                    yearFormat: 'YYYY',
                    monthFormat: 'MM',
                    weekFormat: 'wo',
                    dayFormat: 'DD',
                    hourFormat: 'HH',
                    minuteFormat: 'mm',
                    secondFormat: 'ss',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: false,
                    view: {
                        month: 'Monat',
                        year: 'Jahr',
                        week: 'Woche',
                        day: 'Tag'
                    },
                    month: {
                        long: {
                            January: 'Januar',
                            February: 'Februar',
                            March: 'März',
                            April: 'April',
                            May: 'Mai',
                            June: 'Juni',
                            July: 'Juli',
                            August: 'August',
                            September: 'September',
                            October: 'Oktober',
                            November: 'November',
                            December: 'Dezember'
                        },
                        short: {
                            January: 'Jan',
                            February: 'Feb',
                            March: 'Mär',
                            April: 'Apr',
                            May: 'Mai',
                            June: 'Jun',
                            July: 'Jul',
                            August: 'Aug',
                            September: 'Sep',
                            October: 'Okt',
                            November: 'Nov',
                            December: 'Dez'
                        }
                    },
                    week: {
                        long: {
                            self: 'Woche',
                            monday: 'Montag',
                            tuesday: 'Dienstag',
                            wednesday: 'Mittwoch',
                            thursday: 'Donnerstag',
                            friday: 'Freitag',
                            saturday: 'Samstag',
                            sunday: 'Sonntag'
                        },
                        short: {
                            self: 'Woche',
                            monday: 'Mo',
                            tuesday: 'Di',
                            wednesday: 'Mi',
                            thursday: 'Do',
                            friday: 'Fr',
                            saturday: 'Sa',
                            sunday: 'So'
                        }
                    },
                    placeholder: {
                        date: 'Datum auswählen',
                        week: 'Woche auswählen',
                        month: 'Monat auswählen',
                        year: 'Jahr auswählen',
                        quarter: 'Quartal auswählen',
                        time: 'Zeit auswählen'
                    },
                    rangePlaceholder: {
                        date: ['Startdatum', 'Enddatum'],
                        week: ['Startwoche', 'Endwoche'],
                        month: ['Startmonat', 'Endmonat'],
                        year: ['Startjahr', 'Endjahr'],
                        quarter: ['Startquartal', 'Endquartal'],
                        time: ['Startzeit', 'Endzeit']
                    }
                }
            };
        },
        
        // 获取澳大利亚英语语言配置
        getAustralianLocale() {
            return {
                locale: 'en-AU',
                pagination: {
                    total: 'Total {total} items',
                    pageSize: 'Page Size',
                    goto: 'Go to',
                    page: 'Page',
                    prev: 'Previous',
                    next: 'Next',
                    prev5: 'Previous 5 Pages',
                    next5: 'Next 5 Pages',
                    itemsPerPage: 'items per page',
                    jumpTo: 'Jump to',
                    pageOf: 'Page {current} of {total}',
                    countPerPage: 'items per page'
                },
                datePicker: {
                    now: 'Now',
                    today: 'Today',
                    cancel: 'Cancel',
                    clear: 'Clear',
                    confirm: 'OK',
                    selectDate: 'Select date',
                    selectTime: 'Select time',
                    startDate: 'Start date',
                    endDate: 'End date',
                    startTime: 'Start time',
                    endTime: 'End time',
                    year: 'Year',
                    month: 'Month',
                    week: 'Week',
                    day: 'Day',
                    hour: 'Hour',
                    minute: 'Minute',
                    second: 'Second',
                    yearFormat: 'YYYY',
                    monthFormat: 'MM',
                    weekFormat: 'wo',
                    dayFormat: 'DD',
                    hourFormat: 'HH',
                    minuteFormat: 'mm',
                    secondFormat: 'ss',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: false,
                    view: {
                        month: 'Month',
                        year: 'Year',
                        week: 'Week',
                        day: 'Day'
                    },
                    month: {
                        long: {
                            January: 'January',
                            February: 'February',
                            March: 'March',
                            April: 'April',
                            May: 'May',
                            June: 'June',
                            July: 'July',
                            August: 'August',
                            September: 'September',
                            October: 'October',
                            November: 'November',
                            December: 'December'
                        },
                        short: {
                            January: 'Jan',
                            February: 'Feb',
                            March: 'Mar',
                            April: 'Apr',
                            May: 'May',
                            June: 'Jun',
                            July: 'Jul',
                            August: 'Aug',
                            September: 'Sep',
                            October: 'Oct',
                            November: 'Nov',
                            December: 'Dec'
                        }
                    },
                    week: {
                        long: {
                            self: 'Week',
                            monday: 'Monday',
                            tuesday: 'Tuesday',
                            wednesday: 'Wednesday',
                            thursday: 'Thursday',
                            friday: 'Friday',
                            saturday: 'Saturday',
                            sunday: 'Sunday'
                        },
                        short: {
                            self: 'Week',
                            monday: 'Mon',
                            tuesday: 'Tue',
                            wednesday: 'Wed',
                            thursday: 'Thu',
                            friday: 'Fri',
                            saturday: 'Sat',
                            sunday: 'Sun'
                        }
                    },
                    placeholder: {
                        date: 'Please select date',
                        week: 'Please select week',
                        month: 'Please select month',
                        year: 'Please select year',
                        quarter: 'Please select quarter',
                        time: 'Please select time'
                    },
                    rangePlaceholder: {
                        date: ['Start date', 'End date'],
                        week: ['Start week', 'End week'],
                        month: ['Start month', 'End month'],
                        year: ['Start year', 'End year'],
                        quarter: ['Start quarter', 'End quarter'],
                        time: ['Start time', 'End time']
                    }
                }
            };
        },
        
        // 获取繁体中文语言配置
        getTraditionalChineseLocale() {
            return {
                locale: 'zh-TW',
                pagination: {
                    total: '共 {total} 條',
                    pageSize: '每頁條數',
                    goto: '前往',
                    page: '頁',
                    prev: '上一頁',
                    next: '下一頁',
                    prev5: '向前 5 頁',
                    next5: '向後 5 頁',
                    itemsPerPage: '條/頁',
                    jumpTo: '跳至',
                    pageOf: '第 {current} 頁，共 {total} 頁',
                    countPerPage: '每頁條數'
                },
                datePicker: {
                    now: '此刻',
                    today: '今天',
                    cancel: '取消',
                    clear: '清空',
                    confirm: '確定',
                    selectDate: '選擇日期',
                    selectTime: '選擇時間',
                    startDate: '開始日期',
                    endDate: '結束日期',
                    startTime: '開始時間',
                    endTime: '結束時間',
                    year: '年',
                    month: '月',
                    week: '週',
                    day: '日',
                    hour: '時',
                    minute: '分',
                    second: '秒',
                    yearFormat: 'YYYY年',
                    monthFormat: 'MM月',
                    weekFormat: '第wo週',
                    dayFormat: 'DD日',
                    hourFormat: 'HH時',
                    minuteFormat: 'mm分',
                    secondFormat: 'ss秒',
                    clearFormat: 'YYYY-MM-DD',
                    dateFormat: 'YYYY-MM-DD',
                    dateTimeFormat: 'YYYY-MM-DD HH:mm:ss',
                    monthBeforeYear: true,
                    view: {
                        month: '月',
                        year: '年',
                        week: '週',
                        day: '日'
                    },
                    month: {
                        long: {
                            January: '一月',
                            February: '二月',
                            March: '三月',
                            April: '四月',
                            May: '五月',
                            June: '六月',
                            July: '七月',
                            August: '八月',
                            September: '九月',
                            October: '十月',
                            November: '十一月',
                            December: '十二月'
                        },
                        short: {
                            January: '1月',
                            February: '2月',
                            March: '3月',
                            April: '4月',
                            May: '5月',
                            June: '6月',
                            July: '7月',
                            August: '8月',
                            September: '9月',
                            October: '10月',
                            November: '11月',
                            December: '12月'
                        }
                    },
                    week: {
                        long: {
                            self: '週',
                            monday: '星期一',
                            tuesday: '星期二',
                            wednesday: '星期三',
                            thursday: '星期四',
                            friday: '星期五',
                            saturday: '星期六',
                            sunday: '星期日'
                        },
                        short: {
                            self: '週',
                            monday: '一',
                            tuesday: '二',
                            wednesday: '三',
                            thursday: '四',
                            friday: '五',
                            saturday: '六',
                            sunday: '日'
                        }
                    },
                    placeholder: {
                        date: '請選擇日期',
                        week: '請選擇週',
                        month: '請選擇月份',
                        year: '請選擇年份',
                        quarter: '請選擇季度',
                        time: '請選擇時間'
                    },
                    rangePlaceholder: {
                        date: ['開始日期', '結束日期'],
                        week: ['開始週', '結束週'],
                        month: ['開始月份', '結束月份'],
                        year: ['開始年份', '結束年份'],
                        quarter: ['開始季度', '結束季度'],
                        time: ['開始時間', '結束時間']
                    }
                }
            };
        },
        
        initTheme() {
            const presetTheme = window.__THEME_PRESET__;
            const cachedTheme = this.$util.getCache('userTheme');
            const theme = presetTheme === 'light' ||
                (presetTheme !== 'dark' && cachedTheme === 'light') ? 'light' : 'dark';

            this.$store.commit('updateTheme', theme);
        },
        applyTheme(theme) {
            if (typeof document === 'undefined') {
                return;
            }

            const resolvedTheme = theme === 'light' ? 'light' : 'dark';
            const root = document.documentElement;
            const foreground = resolvedTheme === 'dark' ? '#ffffff' : '#1f1f1f';
            const background = resolvedTheme === 'dark' ? '#1f1f1f' : '#ffffff';

            root.setAttribute('data-theme', resolvedTheme);
            root.classList.remove('theme-light', 'theme-dark');
            root.classList.add(`theme-${resolvedTheme}`);
            root.style.colorScheme = resolvedTheme;
            root.style.backgroundColor = background;
            root.style.color = foreground;

            if (document.body) {
                document.body.style.backgroundColor = background;
                document.body.style.color = foreground;
                document.body.style.opacity = '1';
            }
        },
        autoLogin() {
            common.userinfo().then((res) => {
                if (res.code === 200) {
                    this.userLogin(res.data);
                    // 登录成功后获取账户信息
                    this.getAccountInfo();
                } else {
                    this.userLogin(false);
                    this.$util.delCache('token');
                }
            }).catch((error) => {
                // 处理登录检查失败的情况（如用户未登录、token过期等）
                console.log('🔐 自动登录检查失败:', error.message);
                this.userLogin(false);
                this.$util.delCache('token');
            });
        },
        getServerTime() {
            common.getServerTime().then(res => {
                this.$util.setCache('time_zone', res.data.zone);
            }).catch((error) => {
                console.log('⏰ 获取服务器时间失败:', error.message);
                // 使用本地时区作为备选
            });
        },
        getSiteConfig() {
            common.config(1).then((res) => {
                this.setConfig(res.data);
            }).catch((error) => {
                console.log('⚙️ 获取站点配置失败:', error.message);
                // 使用默认配置
            });
        },
        
        // 设置全局错误处理
        setupGlobalErrorHandling() {
            // 设置全局登录提示函数
            window.triggerLoginRequired = () => {
                // 检查是否已经在登录页面
                if (this.$route.path === '/login') {
                    return;
                }
                
                // 使用统一的公开页面检查
                if (isPublicPath(this.$route.path)) {
                    console.log('ℹ️ 在公开页面，不强制跳转登录');
                    return;
                }
                
                // 显示登录提示
                this.$message.warning(this.$t('common.not_logged_in_please_login'));
                
                // 延迟跳转到登录页面
                setTimeout(() => {
                    this.$router.push('/login');
                }, 1500);
            };
            
            // 处理未捕获的Promise错误
            window.addEventListener('unhandledrejection', (event) => {
                const error = event.reason;
                
                // 如果是401错误，使用统一的处理逻辑
                if (error && error.message && error.message.includes('401')) {
                    const shouldSilentHandle = handle401Error(this.$route.path, () => {
                        if (window.triggerLoginRequired) {
                            window.triggerLoginRequired();
                        }
                    });
                    
                    if (shouldSilentHandle) {
                        console.log('ℹ️ 在公开页面，静默处理401错误');
                        event.preventDefault(); // 阻止默认的错误处理
                        return;
                    }
                    
                    console.log('🔐 在需要登录的页面，触发登录提示');
                    event.preventDefault(); // 阻止默认的错误处理
                    return;
                }
                
                // 其他错误正常处理
                console.error('❌ 未捕获的Promise错误:', error);
            });
            
            // 处理全局JavaScript错误
            window.addEventListener('error', (event) => {
                const error = event.error;
                
                // 如果是401错误，使用统一的处理逻辑
                if (error && error.message && error.message.includes('401')) {
                    const shouldSilentHandle = handle401Error(this.$route.path, () => {
                        if (window.triggerLoginRequired) {
                            window.triggerLoginRequired();
                        }
                    });
                    
                    if (shouldSilentHandle) {
                        console.log('ℹ️ 在公开页面，静默处理401错误');
                        event.preventDefault(); // 阻止默认的错误处理
                        return;
                    }
                    
                    console.log('🔐 在需要登录的页面，触发登录提示');
                    event.preventDefault(); // 阻止默认的错误处理
                    return;
                }
                
                // 其他错误正常处理
                console.error('❌ 全局JavaScript错误:', error);
            });
        },
        getAccountInfo() {
            // 注意：这里仍然使用原来的API，因为这是全局账户信息
            // 盲盒页面的余额会单独调用boxApi.getTradeBalance()
            common.getAccountCurrency().then((res) => {
                if (res.code === 200 && res.data) {
                    console.log('App.vue获取账户信息成功:', res.data);
                    // 处理账户数据
                    const accountsData = this.processAccountData(res.data);
                    this.$store.commit('setAccounts', accountsData);
                } else {
                    console.log('App.vue获取账户信息失败，使用默认数据');
                    this.useDefaultAccountData();
                }
            }).catch((error) => {
                console.log('App.vue获取账户信息异常:', error.message);
                this.useDefaultAccountData();
            });
        },
        processAccountData(data) {
            console.log('App.vue处理账户数据，原始数据:', data);
            
            // 如果data直接包含trade_balance字段，直接使用
            if (data && typeof data === 'object' && data.trade_balance !== undefined) {
                console.log('App.vue直接使用API返回的账户数据');
                return {
                    legal_balance: data.legal_balance || "0.00",
                    balance: data.balance || "0.00",
                    lock_balance: data.lock_balance || "0.00",
                    trade_balance: data.trade_balance || "0.00",
                    lock_trade: data.lock_trade || "0.00",
                    contract_balance: data.contract_balance || "0.00",
                    lock_contract: data.lock_contract || "0.00",
                    future_balance: data.future_balance || "0.00",
                    lock_future: data.lock_future || "0.00",
                    finance_balance: data.finance_balance || "0.00",
                    lock_finance: data.lock_finance || "0.00"
                };
            }
            
            // 如果data是数组，查找USDT的余额
            if (Array.isArray(data)) {
                console.log('App.vue处理数组格式的账户数据');
                const accounts = {
                    legal_balance: "0.00",
                    balance: "0.00",
                    lock_balance: "0.00",
                    trade_balance: "0.00",
                    lock_trade: "0.00",
                    contract_balance: "0.00",
                    lock_contract: "0.00",
                    future_balance: "0.00",
                    lock_future: "0.00",
                    finance_balance: "0.00",
                    lock_finance: "0.00"
                };
                
                // 查找USDT账户 - 通过币种ID 10
                const usdtAccount = data.find(item => {
                    const currencyName = item.currency_name || item.currency || item.name;
                    const currencyId = item.currency_id || item.id;
                    
                    // 优先通过币种ID查找，USDT的ID是10
                    if (currencyId === 10 || currencyId === '10') {
                        return true;
                    }
                    
                    // 备用方案：通过名称查找
                    return currencyName && currencyName.toLowerCase() === 'usdt';
                });
                
                        // 查找DBG账户 - 通过币种ID 20
                const comebitAccount = data.find(item => {
                    const currencyName = item.currency_name || item.currency || item.name;
                    const currencyId = item.currency_id || item.id;
                    
                    // 优先通过币种ID查找，DBG的ID是20
                    if (currencyId === 20 || currencyId === '20') {
                        return true;
                    }
                    
                    // 备用方案：通过名称查找
                    return currencyName && currencyName.toLowerCase() === 'comebit';
                });
                
                if (usdtAccount) {
                    console.log('App.vue找到USDT账户:', usdtAccount);
                    console.log('App.vue USDT账户ID:', usdtAccount.currency_id || usdtAccount.id);
                    console.log('App.vue USDT账户名称:', usdtAccount.currency_name || usdtAccount.currency || usdtAccount.name);
                    console.log('App.vue USDT账户trade_balance字段:', usdtAccount.trade_balance);
                    console.log('App.vue USDT账户lock_trade字段:', usdtAccount.lock_trade);
                    console.log('App.vue USDT账户future_balance字段:', usdtAccount.future_balance);
                    
                    // 设置所有USDT相关余额
                    accounts.trade_balance = usdtAccount.trade_balance || "0.00";
                    accounts.lock_trade = usdtAccount.lock_trade || "0.00";
                    accounts.future_balance = usdtAccount.future_balance || "0.00";
                    accounts.lock_future = usdtAccount.lock_future || "0.00";
                } else {
                    console.log('App.vue未找到USDT账户');
                }
                
                if (comebitAccount) {
                    console.log('App.vue找到DBG账户:', comebitAccount);
                    console.log('App.vue DBG账户ID:', comebitAccount.currency_id || comebitAccount.id);
                    console.log('App.vue DBG账户名称:', comebitAccount.currency_name || comebitAccount.currency || comebitAccount.name);
                    console.log('App.vue DBG账户trade_balance字段:', comebitAccount.trade_balance);
                    console.log('App.vue DBG账户lock_trade字段:', comebitAccount.lock_trade);
                    
                    // 使用comebit_balance字段存储DBG余额
                    accounts.comebit_balance = comebitAccount.trade_balance || "0.00";
                    accounts.lock_comebit = comebitAccount.lock_trade || "0.00";
                } else {
                    console.log('App.vue未找到DBG账户');
                }
                
                if (!usdtAccount && !comebitAccount) {
                    console.log('App.vue未找到USDT和DBG账户，所有账户:', data);
                    console.log('App.vue所有账户的ID和名称:', data.map(item => ({
                        id: item.currency_id || item.id,
                        name: item.currency_name || item.currency || item.name,
                        balance: item.balance
                    })));
                }
                
                return accounts;
            }
            
            console.log('App.vue无法识别的数据格式，使用默认数据');
            return {
                legal_balance: "0.00",
                balance: "0.00",
                lock_balance: "0.00",
                trade_balance: "0.00",
                lock_trade: "0.00",
                contract_balance: "0.00",
                lock_contract: "0.00",
                future_balance: "0.00",
                lock_future: "0.00",
                finance_balance: "0.00",
                lock_finance: "0.00"
            };
        },
        useDefaultAccountData() {
            // 使用默认的账户数据
            const defaultAccounts = {
                legal_balance: "1000.00",
                balance: "1000.00",
                lock_balance: "0.00",
                trade_balance: "500.00",
                lock_trade: "0.00",
                contract_balance: "0.00",
                lock_contract: "0.00",
                future_balance: "0.00",
                lock_future: "0.00",
                finance_balance: "0.00",
                lock_finance: "0.00"
            };
            
            this.$store.commit('setAccounts', defaultAccounts);
            console.log('App.vue使用默认账户数据:', defaultAccounts);
        },
        getLocal() {
            // 关闭自动语言检测，统一使用英文
            // 只初始化语言设置，不根据IP自动检测
            try {
                const _lang = util.getCache('lang');
                if (typeof _lang == 'undefined' || _lang === '') {
                    // 如果没有缓存，设置默认语言为英文
                    this.$i18n.locale = 'en-US';
                    util.setCache('lang', 'en-US');
                    console.log('✅ 设置默认语言为英文');
                } else {
                    // 如果已有缓存语言，使用缓存的语言
                    this.$i18n.locale = _lang;
                    console.log('✅ 使用缓存的语言:', _lang);
                }
            } catch (e) {
                console.error('❌ 语言设置失败:', e);
                // 设置默认语言为英文
                this.$i18n.locale = 'en-US';
                util.setCache('lang', 'en-US');
            }
            
            this.$store.dispatch('initializeDisplayCurrency').catch((error) => {
                console.error('❌ 货币设置失败:', error);
            });
        },
        setupArcoI18n() {
            // 设置ArcoVue的全局国际化配置
            const currentLang = this.$i18n?.locale || 'en-US';
            if (currentLang === 'en-US') {
                // 设置英文
                this.$arcoConfigProvider = {
                    locale: {
                        pagination: {
                            total: 'Total {total} items',
                            pageSize: 'Page Size',
                            goto: 'Go to',
                            page: 'Page',
                            prev: 'Previous',
                            next: 'Next',
                            prev5: 'Previous 5 Pages',
                            next5: 'Next 5 Pages',
                            itemsPerPage: 'items per page',
                            jumpTo: 'Jump to',
                            pageOf: 'Page {current} of {total}'
                        }
                    }
                };
            } else {
                // 设置中文
                this.$arcoConfigProvider = {
                    locale: {
                        pagination: {
                            total: '共 {total} 条',
                            pageSize: '每页条数',
                            goto: '前往',
                            page: '页',
                            prev: '上一页',
                            next: '下一页',
                            prev5: '向前 5 页',
                            next5: '向后 5 页',
                            itemsPerPage: '条/页',
                            jumpTo: '跳至',
                            pageOf: '第 {current} 页，共 {total} 页'
                        }
                    }
                };
            }
        }

    }
}
</script>
<style lang="scss">
#app {
    font-family: "Switzer-Bold";
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #ffffff;
}

/* 全局覆盖白天模式下的输入框边框 */
body.light .arco-input-wrapper .arco-input,
html.light .arco-input-wrapper .arco-input,
[data-theme="light"] .arco-input-wrapper .arco-input {
    border: none !important;
}

body.light .arco-input,
html.light .arco-input,
[data-theme="light"] .arco-input {
    border: none !important;
}

/* 全局覆盖白天模式下的输入框背景 */
body.light .arco-input,
html.light .arco-input,
[data-theme="light"] .arco-input {
    background: transparent !important;
}
</style>
